import React from 'react';

import ArrowSVG from '@pkg/assets/floorGame/arrow.svg?react';
import { type CardSizes, CurrencyIcon, ValuePrefix } from '@pkg/components';
import { type Collectible, OrderStatus, OrderType, Side } from '@pkg/graphql/codegen/graphql';
import { usePricing } from '@pkg/hooks';
import { getBaseCurrencyFromChain } from '@pkg/utils';

import { Icon, Label, PercentLabel, Price, PriceGroup, PriceRow, Value, Wrapper } from './styles';

interface CardPricesProps {
    cardSize?: CardSizes;
    item?: Collectible | null;
    floorPriceWei?: string | null;
    askPrice?: string | null;
    lastSale?: string | null;
    bordered?: boolean;
}

export function CardPrices({ cardSize, item, floorPriceWei, askPrice, lastSale, bordered = false }: CardPricesProps) {
    const { getFormattedPricingValues, getComparisons } = usePricing();
    const { id, orders } = item || { id: '' };
    const [chain] = id.split('.');

    const chainCurrency = getBaseCurrencyFromChain(chain);

    const viableOrderData = orders?.find(
        (order) =>
            order.id?.startsWith('yat') &&
            Date.parse(order.expiresAt) > Date.now() &&
            (order.status === OrderStatus.Pending || order.status === OrderStatus.New),
    );
    const itemPrice = askPrice || viableOrderData?.price;

    const listingPrice =
        viableOrderData?.side === Side.Ask && viableOrderData?.type === OrderType.Limit ? itemPrice : undefined;
    const price = askPrice || listingPrice;

    const { nativeValue } = getFormattedPricingValues({
        weiValue: price,
        currency: chainCurrency,
    });

    const { floorPriceDiff } = floorPriceWei
        ? getComparisons({ weiValue: price, floorPriceWei: lastSale || floorPriceWei })
        : { floorPriceDiff: undefined };

    const prefix = !chainCurrency ? null : (
        <ValuePrefix>
            <CurrencyIcon currency={chainCurrency} theme="dark" />
        </ValuePrefix>
    );
    return itemPrice ? (
        <Wrapper $cardSize={cardSize} $bordered={bordered}>
            <PriceGroup>
                <Label $cardSize={cardSize}>List price</Label>

                <PriceRow>
                    <Price>
                        <Icon $cardSize={cardSize}>{prefix}</Icon>
                        <Value $cardSize={cardSize}>{nativeValue}</Value>

                        {floorPriceDiff ? (
                            <PercentLabel $isDesc={floorPriceDiff < 0}>
                                <ArrowSVG />
                                <span>{Math.abs(floorPriceDiff).toFixed(1)}%</span>
                            </PercentLabel>
                        ) : null}
                    </Price>
                </PriceRow>
            </PriceGroup>
        </Wrapper>
    ) : null;
}
